import { inject, Injectable } from "@angular/core";
import { AnswerDto, ExerciseDto, SequenceResultDto } from "@clients/firestore/dtos/sequence-result.dto";
import { FirestoreClient, SequenceResultsCollectionId } from "@clients/firestore/firestore.client";
import { Answer } from "@domain/Answer";
import { AudioExercise, DropDownExercise, Exercise } from "@domain/Exercise";
import { Result } from "@domain/Result";
import { CurrentSequence, Sequence, SequenceId } from "@domain/Sequence";
import { User } from "@domain/User";

const SEQUENCE_RESULTS_COLLECTION_ID: SequenceResultsCollectionId = "sequence_results";

@Injectable({
  providedIn: "root",
})
export class SequenceResultsClient {
  private firestoreClient = inject(FirestoreClient);

  async createSequence(user: User, sequence: Sequence): Promise<SequenceId> {
    const newSequenceId = await this.firestoreClient.create({ uid: user.id, results: this.mapToDto(sequence) }, SEQUENCE_RESULTS_COLLECTION_ID);

    if (!newSequenceId) throw new Error("Sequence not saved");

    return new SequenceId(newSequenceId);
  }

  async updateSequence(sequence: CurrentSequence) {
    await this.firestoreClient.update({ results: this.mapToDto(sequence) }, SEQUENCE_RESULTS_COLLECTION_ID, sequence.getId().toString());
  }

  private mapToDto(sequence: Sequence): SequenceResultDto[] {
    return sequence.getResults().map(({ exercise, answer, numberOfTries }: Result) => {
      return {
        exercise: this.mapToExerciseDto(exercise),
        answer: this.mapToAnswerDto(answer),
        numberOfTries,
      };
    });
  }

  private mapToExerciseDto(exercise: Exercise): ExerciseDto {
    if (exercise instanceof AudioExercise) {
      return {
        id: exercise.id.toString(),
        sentence: exercise.sentence,
        correctAnswer: exercise.correctAnswer,
        suggestedAnswers: null,
        positiveFeedback: exercise.positiveFeedback,
        constructiveFeedback: exercise.constructiveFeedback,
        audioFileName: exercise.audioFileName,
        type: "AudioExercise",
      };
    }

    if (exercise instanceof DropDownExercise) {
      return {
        id: exercise.id.toString(),
        sentence: exercise.sentence,
        correctAnswer: exercise.correctAnswer,
        suggestedAnswers: exercise.suggestedAnswers,
        positiveFeedback: exercise.positiveFeedback,
        constructiveFeedback: exercise.constructiveFeedback,
        audioFileName: null,
        type: "DropDownExercise",
      };
    }

    throw new Error("Invalid exercise type");
  }

  private mapToAnswerDto(answer: Answer): AnswerDto {
    return {
      value: answer.value,
      state: answer.state,
    };
  }
}
